import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  LinearProgress,
  Alert,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useGraphQL } from '../../apollo/useGraphQL';
import { getMenusForSelect, uploadFileAdmin } from '../../api/driveApi';
import Message from '../../utils/message/Message';

const AdminUploadDialog = ({ open, onClose, currentFolder, onUploadComplete }) => {
  const { executeQuery } = useGraphQL();
  
  // State management
  const [menuOptions, setMenuOptions] = useState([]);
  const [selectedMenuId, setSelectedMenuId] = useState('');
  const [flagLocal, setFlagLocal] = useState(false);
  const [indoor, setIndoor] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState([]);
  const [estimatedTime, setEstimatedTime] = useState(null);
  const [remainingTime, setRemainingTime] = useState(null);

  // Load menu options
  useEffect(() => {
    if (open) {
      loadMenuOptions();
    }
  }, [open]);

  const loadMenuOptions = async () => {
    try {
      const menus = await getMenusForSelect(executeQuery);
      setMenuOptions(menus);
      // Set first menu as default if available
      if (menus.length > 0) {
        setSelectedMenuId(menus[0].menuId);
      }
    } catch (error) {
      console.error('Error loading menu options:', error);
      Message.showError({ message: '메뉴 목록을 불러오는 중 오류가 발생했습니다.' });
    }
  };

  // File selection handler
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
    // Reset input
    event.target.value = '';
  };

  // Remove file from selection
  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Calculate estimated upload time (standard estimate)
  const calculateEstimatedTime = (totalBytes) => {
    // Standard upload speed estimates (bytes per second)
    // Typical broadband: 2-5 MB/s, we'll use 2MB/s as standard
    const standardSpeedBps = 2 * 1024 * 1024; // 2 MB/s
    const baseTimeSeconds = totalBytes / standardSpeedBps;

    // Add 20% buffer for processing overhead and network variability
    const bufferedTime = baseTimeSeconds * 1.2;

    // Add minimum 3 seconds for server processing
    return Math.max(bufferedTime + 3, 5); // Minimum 5 seconds
  };

  // Format time duration
  const formatTime = (seconds) => {
    if (seconds < 60) {
      return `${Math.ceil(seconds)}초`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.ceil(seconds % 60);
      return remainingSeconds > 0 ? `${minutes}분 ${remainingSeconds}초` : `${minutes}분`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return minutes > 0 ? `${hours}시간 ${minutes}분` : `${hours}시간`;
    }
  };

  // Update estimated time when files change
  useEffect(() => {
    if (selectedFiles.length > 0) {
      const totalBytes = selectedFiles.reduce((sum, file) => sum + file.size, 0);
      const estimated = calculateEstimatedTime(totalBytes);
      setEstimatedTime(estimated);
    } else {
      setEstimatedTime(null);
    }
  }, [selectedFiles]);

  // Upload files
  const handleUpload = async () => {
    // 업로드 버튼 클릭 시점 로깅 (LocalDateTime 형식 + 밀리초)
    const uploadClickTime = new Date();
    const localDateTime = uploadClickTime.getFullYear() + '-' +
      String(uploadClickTime.getMonth() + 1).padStart(2, '0') + '-' +
      String(uploadClickTime.getDate()).padStart(2, '0') + 'T' +
      String(uploadClickTime.getHours()).padStart(2, '0') + ':' +
      String(uploadClickTime.getMinutes()).padStart(2, '0') + ':' +
      String(uploadClickTime.getSeconds()).padStart(2, '0') + '.' +
      String(uploadClickTime.getMilliseconds()).padStart(3, '0');

    console.log('업로드 버튼 클릭 시점 (LocalDateTime):', localDateTime);

    if (!selectedMenuId) {
      Message.showError({ message: '메뉴를 선택해주세요.' });
      return;
    }

    if (selectedFiles.length === 0) {
      Message.showError({ message: '업로드할 파일을 선택해주세요.' });
      return;
    }

    setUploading(true);
    setErrors([]);
    const results = [];
    const uploadStartTime = Date.now();

    // Set initial remaining time
    setRemainingTime(estimatedTime);

    // Timer for updating remaining time
    const timeUpdateInterval = setInterval(() => {
      const elapsedSeconds = (Date.now() - uploadStartTime) / 1000;
      const remaining = Math.max(estimatedTime - elapsedSeconds, 0);
      setRemainingTime(remaining);
    }, 1000);

    try {
      // 모든 파일을 선택된 상태로 표시
      selectedFiles.forEach((_, index) => {
        setUploadProgress(prev => ({
          ...prev,
          [index]: { status: 'uploading', progress: 50 }
        }));
      });

      // Upload all files at once using the new API
      try {
        const result = await uploadFileAdmin(selectedMenuId, currentFolder || '', flagLocal, selectedFiles, indoor);
        
        // 모든 파일 업로드 성공으로 처리
        selectedFiles.forEach((file, index) => {
          results.push({ file: file.name, success: true, result });
          setUploadProgress(prev => ({
            ...prev,
            [index]: { status: 'success', progress: 100 }
          }));
        });
        
      } catch (error) {
        // 모든 파일 업로드 실패로 처리
        selectedFiles.forEach((file, index) => {
          results.push({ file: file.name, success: false, error: error.message });
          setUploadProgress(prev => ({
            ...prev,
            [index]: { status: 'error', progress: 0 }
          }));
          setErrors(prev => [...prev, `${file.name}: ${error.message}`]);
        });
      }

      // Show results
      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      if (successCount > 0) {
        // Message.showSuccess({ 
        //   message: `${successCount}개 파일이 성공적으로 업로드되었습니다.` 
        // });
      }
      
      if (failCount > 0) {
        Message.showError({ 
          message: `${failCount}개 파일 업로드에 실패했습니다.` 
        });
      }

      // Call upload complete callback
      if (onUploadComplete) {
        onUploadComplete();
      }

      // Close dialog if all uploads succeeded
      if (failCount === 0) {
        handleClose();
      }

    } catch (error) {
      console.error('Upload error:', error);
      Message.showError({ message: '업로드 중 오류가 발생했습니다.' });
    } finally {
      clearInterval(timeUpdateInterval);
      setUploading(false);
      setRemainingTime(null);
    }
  };

  // Close dialog and reset state
  const handleClose = () => {
    if (!uploading) {
      setSelectedFiles([]);
      setSelectedMenuId('');
      setFlagLocal(false);
      setIndoor(false);
      setUploadProgress({});
      setErrors([]);
      setEstimatedTime(null);
      setRemainingTime(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '500px',
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CloudUploadIcon />
        파일 업로드 (관리자)
      </DialogTitle>

      <DialogContent sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Menu Selection */}
          <FormControl fullWidth>
            <InputLabel>메뉴 선택</InputLabel>
            <Select
              value={selectedMenuId}
              onChange={(e) => setSelectedMenuId(e.target.value)}
              label="메뉴 선택"
            >
              {menuOptions.map((menu) => (
                <MenuItem key={menu.menuId} value={menu.menuId}>
                  {menu.menuName} ({menu.menuId})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Flag Local and Indoor Switches */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Flag Local Switch */}
            <Box sx={{ flex: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={flagLocal}
                    onChange={(e) => setFlagLocal(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">로컬</Typography>
                    <Typography variant="caption" color="text.secondary">
                      로컬 저장소 사용 여부
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {/* Indoor Switch - Only show for pc-density menu */}
            {selectedMenuId === 'pc-density' && (
              <Box sx={{ flex: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={indoor}
                      onChange={(e) => setIndoor(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">실내</Typography>
                      <Typography variant="caption" color="text.secondary">
                        실내/단일 객체 구분 필드
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}
          </Box>

          {/* File Selection */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              파일 선택
            </Typography>
            
            <input
              accept="*/*"
              style={{ display: 'none' }}
              id="admin-file-input"
              multiple
              type="file"
              onChange={handleFileSelect}
              disabled={uploading}
            />
            
            <label htmlFor="admin-file-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<AddIcon />}
                disabled={uploading}
                sx={{ mb: 2 }}
              >
                파일 추가
              </Button>
            </label>

            {/* Selected Files List */}
            {selectedFiles.length > 0 && (
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
                <List dense>
                  {selectedFiles.map((file, index) => {
                    const progress = uploadProgress[index];
                    return (
                      <ListItem key={index} divider>
                        <ListItemIcon>
                          <FileIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={file.name}
                          secondary={
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {formatFileSize(file.size)}
                              </Typography>
                              {progress && (
                                <Box sx={{ mt: 1 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={progress.progress}
                                    color={
                                      progress.status === 'success' ? 'success' :
                                      progress.status === 'error' ? 'error' : 'primary'
                                    }
                                  />
                                  <Typography variant="caption">
                                    {progress.status === 'uploading' && '업로드 중...'}
                                    {progress.status === 'success' && '완료'}
                                    {progress.status === 'error' && '실패'}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handleRemoveFile(index)}
                            disabled={uploading}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            )}
          </Box>

          {/* Upload Summary */}
          {selectedFiles.length > 0 && (
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>업로드 설정:</strong>
              </Typography>
              <Typography variant="body2">
                메뉴: {menuOptions.find(m => m.menuId === selectedMenuId)?.menuName || 'N/A'}
              </Typography>
              <Typography variant="body2">
                로컬 플래그: {flagLocal ? '활성화' : '비활성화'}
              </Typography>
              {selectedMenuId === 'pc-density' && (
                <Typography variant="body2">
                  실내 구분: {indoor ? '실내' : '실외'}
                </Typography>
              )}
              <Typography variant="body2">
                선택된 파일: {selectedFiles.length}개
              </Typography>
              <Typography variant="body2">
                총 크기: {formatFileSize(selectedFiles.reduce((sum, file) => sum + file.size, 0))}
              </Typography>
              {estimatedTime && (
                <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
                  예상 업로드 시간: {formatTime(estimatedTime)}
                </Typography>
              )}
            </Box>
          )}

          {/* Upload Progress with Remaining Time */}
          {uploading && remainingTime !== null && (
            <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px solid #1976d2' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  업로드 진행 중...
                </Typography>
                <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
                  남은 시간: {remainingTime > 0 ? formatTime(remainingTime) : '완료 중...'}
                </Typography>
              </Box>
              <LinearProgress
                variant="indeterminate"
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4
                  }
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                서버에서 파일을 처리하고 있습니다. 잠시만 기다려 주세요.
              </Typography>
            </Box>
          )}

          {/* Error Messages */}
          {errors.length > 0 && (
            <Alert severity="error">
              <Typography variant="body2" sx={{ mb: 1 }}>
                다음 파일들의 업로드에 실패했습니다:
              </Typography>
              {errors.map((error, index) => (
                <Typography key={index} variant="caption" display="block">
                  • {error}
                </Typography>
              ))}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={uploading}>
          {uploading ? '업로드 중...' : '취소'}
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={uploading || selectedFiles.length === 0 || !selectedMenuId}
          startIcon={<CloudUploadIcon />}
        >
          {uploading ? '업로드 중...' : `업로드 (${selectedFiles.length}개)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminUploadDialog;